/**
 * @file WaiterTopBar.tsx
 * @description Top navigation bar for the waiter terminal with TableGreet brand identity.
 * Replaces floor plan with primary operational navigation: Orders, Calls, and Reservations.
 */

import React from 'react';
import { Bell, TrendingUp, LogOut, CheckCircle2, User, UtensilsCrossed, Calendar } from 'lucide-react';
import logo from '../../assets/logo.svg';

interface WaiterTopBarProps {
  venueName: string;
  waiterName: string;
  activeNavTab: 'orders' | 'calls' | 'reservations';
  onChangeNavTab: (tab: 'orders' | 'calls' | 'reservations') => void;
  ordersCount: number;
  pendingCallsCount: number;
  reservationsCount: number;
  readyOrdersCount: number;
  onOpenNotifications: () => void;
  onOpenStats: () => void;
  onLogout: () => void;
}

export const WaiterTopBar: React.FC<WaiterTopBarProps> = ({
  venueName,
  waiterName,
  activeNavTab,
  onChangeNavTab,
  ordersCount,
  pendingCallsCount,
  reservationsCount,
  readyOrdersCount,
  onOpenNotifications,
  onOpenStats,
  onLogout,
}) => {
  return (
    <header className="bg-[#121212] border-b border-white/10 px-4 py-3 shrink-0">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Real TableGreet Logo & Waiter Identity */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} className="w-9 h-9 object-contain shrink-0" alt="TableGreet™" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-black text-base tracking-tight bg-gradient-to-r from-[#ff4f00] to-orange-400 bg-clip-text text-transparent">
                  TableGreet<sup className="text-[0.55em] font-bold align-super">™</sup>
                </span>
                <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-[#ff4f00]/15 text-[#ff4f00] border border-[#ff4f00]/30 tracking-wider">
                  Waiter
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-0.5 truncate max-w-[200px] xs:max-w-[240px] sm:max-w-none">
                <span className="text-zinc-200 font-semibold uppercase truncate">{venueName}</span>
                <span>•</span>
                <User className="w-3 h-3 text-zinc-500 shrink-0" />
                <span className="font-semibold text-zinc-300 truncate">{waiterName}</span>
                <span className="hidden xs:inline">•</span>
                <span className="hidden xs:inline text-emerald-400 font-medium">On Shift</span>
              </div>
            </div>
          </div>

          {/* Mobile Quick Action Buttons */}
          <div className="flex md:hidden items-center gap-1.5 shrink-0">
            {readyOrdersCount > 0 && (
              <button
                type="button"
                onClick={onOpenNotifications}
                className="flex items-center gap-1 px-2.5 py-1.5 min-h-[38px] rounded-xl bg-[#ff4f00] text-white text-xs font-black animate-pulse cursor-pointer shadow-lg shadow-[#ff4f00]/25"
                title="Ready Orders"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{readyOrdersCount}</span>
              </button>
            )}

            <button
              type="button"
              onClick={onOpenNotifications}
              className="relative p-2 min-w-[38px] min-h-[38px] flex items-center justify-center rounded-xl bg-[#181818] border border-white/10 text-zinc-300 cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
              {pendingCallsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white font-mono font-black text-[10px] flex items-center justify-center">
                  {pendingCallsCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={onOpenStats}
              className="p-2 min-w-[38px] min-h-[38px] flex items-center justify-center rounded-xl bg-[#181818] border border-white/10 text-zinc-300 cursor-pointer"
              title="My Shift"
            >
              <TrendingUp className="w-4 h-4 text-[#ff4f00]" />
            </button>

            <button
              type="button"
              onClick={onLogout}
              className="p-2 min-w-[38px] min-h-[38px] flex items-center justify-center rounded-xl bg-[#181818] hover:bg-rose-950/60 border border-white/10 text-zinc-400 hover:text-rose-300 transition-colors cursor-pointer"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Primary Navigation Tabs: Orders, Calls, Reservations */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          {/* Orders Tab */}
          <button
            type="button"
            onClick={() => onChangeNavTab('orders')}
            className={`min-h-[42px] px-3.5 sm:px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap active:scale-[0.98] ${
              activeNavTab === 'orders'
                ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30'
                : 'bg-[#181818] border border-white/10 text-zinc-400 hover:text-white'
            }`}
          >
            <UtensilsCrossed className="w-3.5 h-3.5" />
            <span>Orders</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                activeNavTab === 'orders' ? 'bg-black/30 text-white' : 'bg-white/10 text-zinc-300'
              }`}
            >
              {ordersCount}
            </span>
          </button>

          {/* Calls Tab */}
          <button
            type="button"
            onClick={() => onChangeNavTab('calls')}
            className={`min-h-[42px] px-3.5 sm:px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap active:scale-[0.98] ${
              activeNavTab === 'calls'
                ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30'
                : 'bg-[#181818] border border-white/10 text-zinc-400 hover:text-white'
            }`}
          >
            <Bell className={`w-3.5 h-3.5 ${pendingCallsCount > 0 ? 'text-amber-400 animate-bounce' : ''}`} />
            <span>Calls</span>
            {pendingCallsCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-rose-950 text-rose-200 border border-rose-800 animate-pulse">
                {pendingCallsCount}
              </span>
            )}
          </button>

          {/* Reservations Tab */}
          <button
            type="button"
            onClick={() => onChangeNavTab('reservations')}
            className={`min-h-[42px] px-3.5 sm:px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap active:scale-[0.98] ${
              activeNavTab === 'reservations'
                ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30'
                : 'bg-[#181818] border border-white/10 text-zinc-400 hover:text-white'
            }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Reservations</span>
            {reservationsCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                {reservationsCount}
              </span>
            )}
          </button>
        </div>

        {/* Action Controls & Waiter Shift */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {/* Notifications Bell */}
          <button
            type="button"
            onClick={onOpenNotifications}
            className="relative p-2.5 rounded-xl bg-[#181818] hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white transition-colors cursor-pointer"
            title="Service Alerts"
          >
            <Bell className="w-4 h-4" />
            {pendingCallsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white font-mono font-black text-[10px] flex items-center justify-center animate-pulse">
                {pendingCallsCount}
              </span>
            )}
          </button>

          {/* Shift Performance */}
          <button
            type="button"
            onClick={onOpenStats}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#181818] hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white text-xs font-bold transition-colors cursor-pointer"
          >
            <TrendingUp className="w-3.5 h-3.5 text-[#ff4f00]" />
            <span>My Shift</span>
          </button>

          {/* Sign Out */}
          <button
            type="button"
            onClick={onLogout}
            className="p-2 rounded-xl bg-[#181818] hover:bg-rose-950/60 border border-white/10 text-zinc-400 hover:text-rose-300 transition-colors cursor-pointer"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
